<script lang="ts">
    import { get } from "lodash"
    import { createEventDispatcher } from "svelte"
    import OperationPositions from "../routes/operations/OperationPositions.svelte"
    import { icons } from "../../config"
    import { translatePath } from "../../operationMapping"
    import { mdiChevronDown, mdiChevronUp, mdiTag } from "@mdi/js"
    import Modal from "./Modal.svelte"
    import Icon from "./Icon.svelte"
    import { navigateTo, tooltip } from "../../helper"
    import { getAttachement } from "../../api"
    import MiniLoader from "../widgets/MiniLoader.svelte"
    import { onMount } from "svelte"
    import { _ } from "svelte-i18n"

    export let gridview = false,
        id = "",
        deactivateSort = false,
        className = "",
        variable_height = false,
        sort = "id",
        cartId = "",
        cart: Cart = null,
        inCart = false,
        columns: TableColumn[],
        showPositions = false,
        negativeTop = false,
        buttonsColumn: {
            label?: string
            className?: string
        } = null,
        followUpColumn: {
            label?: string
            className?: string
        } = null,
        buttons: TableButton[] | any = null,
        followUpButton: TableButton<"operation | operation-out"> = null,
        rows: TableRows

    $: _buttons = buttons?.filter((b: { hide: any }) => !b.hide)
    const dispatch = createEventDispatcher()

    /**
     * Verengt eine Tabellenzeile auf Operation/OperationOut für
     * OperationPositions. Nur diese Zeilentypen tragen `positions`; der
     * -Zweig ist ausschließlich im Warenkorb aktiv (Cart.svelte setzt
     * showPositions), dessen rows Operation[] sind.
     */
    function alsOperation(zeile: TableRow): Operation | OperationOut | null {
        if (zeile && "positions" in zeile) {
            return zeile
        }
        return null
    }

    onMount(() => dispatch("mounted"))
    function sortBy(s: string) {
        if (!s) return
        const sortOrderAndFieldMatch = sort?.match(/^(\-?)(.+)$/) || []
        sort = s === sortOrderAndFieldMatch[2] ? (sortOrderAndFieldMatch[1] ? sortOrderAndFieldMatch[2] : `-${s}`) : s
        dispatch("sort", sort)
    }
    let headers: NodeListOf<HTMLElement>
    let currentHeader: HTMLElement
    function updateVisibleHeader() {
        if (gridview) return
        let headerToShow: HTMLElement = null
        headers.forEach((header) => {
            const tableRect = header.getBoundingClientRect()
            const sep = document.getElementById(`sep${header.id}`)?.getBoundingClientRect()
            if (tableRect.top <= window.innerHeight && tableRect.top >= 0 && tableRect.bottom <= sep?.top) {
                if (!headerToShow || tableRect.top < headerToShow.getBoundingClientRect().top) {
                    headerToShow = header
                    currentHeader = header
                }
            }
        })

        headers.forEach((header) => {
            if (header == headerToShow) {
                if (header.classList.contains("default")) {
                    header.classList.remove("default")
                    header.classList.add("stick")
                }
            } else {
                if (header.classList.contains("stick")) {
                    header.classList.remove("stick")
                    header.classList.add("default")
                }
            }
        })
    }
    onMount(updateVisibleHeader)

    function updateHeaders() {
        headers = document.querySelectorAll(".tablerow.thead")
    }

    let hoverHeader: HTMLElement | null = null

    let timeoutId: NodeJS.Timeout
    let trowHover: boolean
    function makeSticky(id: string, trow: boolean) {
        const header = document.getElementById(id)
        hoverHeader = header
        if (trow) trowHover = true
        if (trowClick) return
        if (gridview) return
        if (header && !header.classList.contains("hover-stick")) {
            header.classList.add("hover-stick")
        }
    }

    function removeSticky(id: string, trow: boolean) {
        if (trow) trowHover = false
        if (trowClick) return
        if (gridview) return
        const header = document.getElementById(id)
        if (header && header.classList.contains("hover-stick")) {
            timeoutId = setTimeout(() => {
                if (!trowHover) {
                    hoverHeader = null
                    header.classList.remove("hover-stick")
                }
            }, 100) // Set a delay before removing the sticky class
        }
    }
    let trowClick = true

    function toggleSticky(id: string) {
        hoverHeader = null
        if (gridview) return
        const header = document.getElementById(id)

        if (!trowClick) {
            // If trowClick is false
            if (header && !header.classList.contains("hover-stick")) {
                header.classList.add("hover-stick")
                hoverHeader = header
            }
            trowClick = true // Set it to true
        } else {
            // If trowClick is true
            if (header && header.classList.contains("hover-stick")) {
                header.classList.remove("hover-stick")
            }
            trowClick = false // Set it back to false
        }
    }

    $: rows, updateHeaders()
    let lastClickedRow: TableRow
    let opened: any[] = []
    rows.forEach((r) => opened.push(true))
    let attachmentsProm: {
        [opNum: number]: Promise<{ count?: number; data: Attachement[] }>
    } = {}

    const getPrevious = (previous: string, current = "previous") => {
        if (previous == "null")
            return current == "current" ? $_("components.table.noCurrentEntry") : $_("components.table.noEarlierEntry")
        if (previous == "true") return $_("general.yes")
        if (previous == "false") return $_("general.no")
        return previous
    }
    async function loadAttachments(op: TableRow<"operation | operation-out">) {
        if (!attachmentsProm[op.number]) {
            attachmentsProm[op.number] = getAttachement(op.type == 2 ? op.id : op.number)
        }
        try {
            const r = await attachmentsProm[op.number]
            return r.data
        } catch (e) {}
        return []
    }

    let showOperationChanges: Operation | OperationOut
    let outerWidth: number
</script>

<svelte:window on:scroll="{updateVisibleHeader}" bind:innerWidth="{outerWidth}" />

<div
    class="table {className} {location.pathname.includes('cart') ? 'cart-table' : 'op-table'}"
    class:tableview="{!gridview}"
    class:gridview
    {id}
    style="{location.pathname.includes('cart') ? 'z-index:9998;' : ''}"
>
    {#each rows || [] as r, i (`${r.id}${i}`)}
        {#if "updateLogs" in r && Array.isArray(r?.updateLogs) && r?.updateLogs?.length}
            <div class="offer-changed-text">
                {$_("components.table.offerGotChanged", {
                    values: {
                        offerNumber: r?.number,
                    },
                })}
                <button
                    style="margin-left: 30px; font-style: italic; padding: 0px 10px !important; min-width: 44px; display: flex; justify-content: center; height: 30px;"
                    class="btn btn-outline"
                    on:keydown
                    on:click="{() => {
                        showOperationChanges = r
                    }}"
                >
                    i
                </button>
            </div>
        {/if}
        {#if "operationOutOfferReference" in r && r?.operationOutOfferReference?.number && location.pathname.includes("cart")}
            <div class="offer-changed-text">
                {$_("components.table.transationStemsFrom")}:
                <button
                    style="margin-left: 15px; font-style: italic; padding: 0px 10px !important; min-width: 44px; display: flex; justify-content: center; height: 30px; color: red;"
                    class="transparent btn btn-link"
                    on:keydown
                    on:click="{() => {
                        navigateTo(
                            `/de/operations-out?type=1&id=` +
                                ('operationOutOfferReference' in r ? r?.operationOutOfferReference?.id : '')
                        )
                    }}"
                >
                    <Icon path="{mdiTag}" />{r?.operationOutOfferReference?.number}
                </button>
            </div>
        {/if}
        {#if showPositions || i == 0}
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <div
                class="tablerow thead default"
                data-cy="table-header"
                id="{r.id}{cartId}"
                style="{negativeTop ? 'top: -20px !important;' : ''}"
                on:mouseenter="{() => makeSticky(`${r.id}${cartId}`, false)}"
                on:mouseleave="{() => removeSticky(`${r.id}${cartId}`, false)}"
                on:keydown
            >
                {#if showPositions}
                    <!-- svelte-ignore a11y-no-static-element-interactions -->
                    <div
                        class="opener"
                        on:keydown
                        on:click|stopPropagation="{() => {
                            opened[i] = !opened[i]

                            toggleSticky(`${r.id}${cartId}`)
                        }}"
                    >
                        {#if opened[i]}
                            <div class="chevron">
                                <Icon path="{mdiChevronUp}" size="25px" />
                            </div>
                        {:else}
                            <div class="chevron">
                                <Icon path="{mdiChevronDown}" size="25px" />
                            </div>
                        {/if}
                    </div>
                {/if}

                {#if gridview || (hoverHeader && hoverHeader.id == `${r.id}${cartId}`) || opened[i] || !showPositions}
                    {#each columns as col}
                        {#if !col.hide}
                            <div
                                class="col {col.className || ''} {col.align ? 'align' + col.align : ''}"
                                class:sortable="{col.sortable}"
                                class:sortactive="{sort == col.field || sort == '-' + col.field}"
                                use:tooltip="{col.headerTooltip && {
                                    content: col.headerTooltip,
                                }}"
                            >
                                <!-- svelte-ignore a11y-click-events-have-key-events -->
                                <!-- svelte-ignore a11y-no-static-element-interactions -->
                                <div
                                    class="cell_inner"
                                    on:click="{() => {
                                        sortBy(col.sortable && col.field)
                                    }}"
                                >
                                    {@html col.label}{#if col.sortable && !deactivateSort}<span class="sorticon"
                                            ><Icon
                                                path="{sort == col.field
                                                    ? icons.sortDown
                                                    : sort == '-' + col.field
                                                      ? icons.sortUp
                                                      : icons.sortUpDown}"
                                            /></span
                                        >{/if}
                                </div>
                            </div>
                        {/if}
                    {/each}

                    {#if _buttons?.length}
                        <div class="col {buttonsColumn?.className || ''} aligncenter">
                            <div class="cell_inner">{buttonsColumn?.label || ""}</div>
                        </div>
                    {/if}
                    {#if !!followUpButton?.label}
                        <div class="col {followUpColumn?.className || ''} aligncenter">
                            <!-- svelte-ignore a11y-click-events-have-key-events -->
                            <div
                                class="cell_inner"
                                on:click="{() => {
                                    if (followUpButton.sort) {
                                        if (sort == 'followUpButton.sort') sort = '-followUpButton.sort'
                                        else if (sort == '-followUpButton.sort') sort = 'followUpButton.sort'
                                        else sort = 'followUpButton.sort'
                                        dispatch('followUpButton-sort', sort)
                                    }
                                }}"
                            >
                                {followUpButton?.label || ""}
                                {#if followUpButton.sort}
                                    <span class="sorticon">
                                        <Icon
                                            path="{sort == 'followUpButton.sort'
                                                ? icons.sortDown
                                                : sort == '-followUpButton.sort'
                                                  ? icons.sortUp
                                                  : icons.sortUpDown}"
                                        />
                                    </span>
                                {/if}
                            </div>
                        </div>
                    {/if}
                {:else}
                    <div style="padding-left: 30px;">
                        {$_("components.table.offerNumber")}: {"number" in r ? r.number : ""}
                    </div>
                {/if}
            </div>
        {/if}

        <!-- svelte-ignore a11y-click-events-have-key-events -->
        {#if gridview || (hoverHeader && hoverHeader.id == `${r.id}${cartId}`) || opened[i] || !showPositions}
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <div
                class="tablerow trow {variable_height ? 'variable_height' : ''} {opened[i]
                    ? 'open'
                    : showPositions
                      ? 'close'
                      : 'open'} "
                class:lastclicked="{r == lastClickedRow}"
                data-cy="table-row"
                on:mouseenter="{() => makeSticky(`${r.id}${cartId}`, true)}"
                on:mouseleave="{() => removeSticky(`${r.id}${cartId}`, true)}"
                on:click="{() => {
                    lastClickedRow = r
                    dispatch('click', r)
                }}"
            >
                {#if gridview && showPositions}
                    <!-- svelte-ignore a11y-no-static-element-interactions -->
                    <div
                        class="opener"
                        on:click|stopPropagation="{() => {
                            opened[i] = !opened[i]
                        }}"
                    >
                        {#if opened[i]}
                            <div class="chevron">
                                <Icon path="{mdiChevronUp}" size="42px" />
                            </div>
                        {:else}
                            <div class="chevron">
                                <Icon path="{mdiChevronDown}" size="42px" />
                            </div>
                        {/if}
                    </div>
                {/if}
                {#key rows}
                    {#each columns as col}
                        {#if !col.hide}
                            <div
                                class="col {col.className || ''} {col.align
                                    ? 'align' + col.align
                                    : ''} {col.getClasses && col.getClasses(r)}"
                                use:tooltip="{col.tooltipComponent
                                    ? {
                                          overflowContent: get(r, col.field) || '',
                                          content: {
                                              component: col.tooltipComponent,
                                              props: col.getTooltipProps && col.getTooltipProps(r, col.field),
                                          },
                                      }
                                    : !col.component && {
                                          content: 'invalid',
                                          overflowContent: get(r, col.field) || '',
                                      }}"
                            >
                                {#if gridview}
                                    <span class="label">{col.mobileLabel || col.label}</span>
                                {/if}
                                {#if col.component}
                                    <svelte:component
                                        this="{col.component}"
                                        {...col.getProps && col.getProps(r, col.field)}
                                    />
                                {:else}
                                    <div style="width: 100%;">
                                        {get(r, col.field) == null || get(r, col.field) == undefined
                                            ? ""
                                            : get(r, col.field)}
                                    </div>
                                {/if}
                            </div>
                        {/if}
                    {/each}
                {/key}

                {#if _buttons?.length}
                    <div class="col action_buttons {buttonsColumn?.className || ''} ">
                        {#each _buttons as b}
                            <div
                                use:tooltip="{b.getDisabledTooltip && b.disabled && b.disabled(r)
                                    ? b.getDisabledTooltip(r)
                                    : false}"
                            >
                                <button
                                    class="btn {b.className || ''}"
                                    on:click|stopPropagation="{() => b.onClick(r)}"
                                    use:tooltip="{b.getTooltip && b.getTooltip(r)}"
                                    disabled="{b.disabled && b.disabled(r)}"
                                >
                                    {#if b.component}
                                        <svelte:component
                                            this="{b.component}"
                                            {...b}
                                            {...b.getComponentProps ? b.getComponentProps(r) : {}}
                                        />
                                    {:else}
                                        <Icon size="24px" path="{b.svgPath}" />
                                        <span class="label">{b.label || ""}</span>
                                    {/if}
                                </button>
                            </div>
                        {/each}
                    </div>
                {/if}
                {#if !!followUpButton?.label}
                    <div class="col action_buttons_follow_up mw-160 max-w-180 w-160">
                        {#if gridview}
                            <span class="label">{$_("components.table.watchlist")}</span>
                        {/if}
                        <svelte:component
                            this="{followUpButton.component}"
                            {...followUpButton.getComponentProps
                                ? followUpButton.getComponentProps(alsOperation(r))
                                : {}}
                        />
                    </div>
                {/if}
            </div>
        {/if}
        {#if showPositions}
            {#if !rows}
                <div style="padding-top: 50px; padding-left: 50px;">
                    <MiniLoader />
                </div>
            {:else if alsOperation(r)}
                <OperationPositions
                    grid="{gridview}"
                    rowId="{r.id}"
                    cart="{true}"
                    {inCart}
                    cartObj="{cart}"
                    {cartId}
                    operation="{alsOperation(r)}"
                    activePosition="{0}"
                    {loadAttachments}
                    on:positionSeletion="{(e) => {
                        let op = e.detail.operation
                        rows[i] = op
                        dispatch('positionChange', { operations: rows })
                    }}"
                    on:operationDeletion="{(e) => {
                        rows.splice(i, 1)
                        dispatch('operationDeletion', { operations: rows })
                    }}"
                    on:click="{(e) => {
                        lastClickedRow = r
                        dispatch('click', {
                            ...r,
                            gridview: gridview,
                            activePosition: e.detail.activePosition,
                            operation: r,
                        })
                    }}"
                />
            {/if}
            <div class="margin-lg border-sep" id="{`sep${r.id}${cartId}`}"></div>
        {/if}
    {/each}
</div>

{#if showOperationChanges && "updateLogs" in showOperationChanges}
    <Modal show="{true}" on:close="{() => (showOperationChanges = null)}" showCloseIcon="{true}">
        <div slot="header">{$_("components.table.changesInOperations")}</div>
        <div class="operation-changes">
            <div class="grid-container">
                <div class="row headline">
                    <div class="grid-item header mobile-hide">{$_("components.table.fieldName")}</div>
                    <div class="grid-item header">{$_("components.table.previousValue")}</div>
                    <div class="grid-item header">{$_("components.table.currentValue")}</div>
                </div>
                {#each showOperationChanges?.updateLogs as log, i (i)}
                    <div class="row items">
                        <div class="grid-item">
                            <div class="header">{$_("components.table.fieldName")}</div>
                            <div>{@html translatePath(log.field)}</div>
                        </div>
                        <div class="grid-item">
                            <div class="header">{$_("components.table.previousValue")}</div>
                            <div>{getPrevious(log.previous)}</div>
                        </div>
                        <div class="grid-item">
                            <div class="header">{$_("components.table.currentValue")}</div>
                            <div>{getPrevious(log.current, "current")}</div>
                        </div>
                    </div>
                {/each}
            </div>
        </div>
    </Modal>
{/if}
